<template>
  <div class="border rounded-lg" :class="panelClasses">
    <!-- Header (clickable to toggle) -->
    <div
      class="flex justify-between items-center p-2 cursor-pointer hover:bg-gray-50"
      @click="toggleExpanded"
    >
      <div class="flex items-center gap-3 flex-grow">
        <!-- Expand/Collapse icon -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="w-5 h-5 transition-transform"
          :class="{ 'rotate-90': isExpanded }"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
          />
        </svg>

        <div>
          <h2 class="text-xl font-bold">
            {{ portalName }}
            <span
              v-if="!isExpanded && selectedPermissions.length === 0"
              class="text-sm text-gray-400 font-normal italic"
            >
               - No permissions
            </span>
          </h2>
          <p v-if="!hasRegistration && isExpanded" class="text-sm text-gray-500 italic">
            No registration - will be created on save
          </p>
          <p
            v-if="!isExpanded && selectedPermissions.length > 0"
            class="text-sm text-gray-600"
          >
            {{ selectedPermissions.length }} permission(s)
          </p>
        </div>
      </div>

      <!-- Status indicator -->
      <div class="flex items-center gap-2">
        <span
          v-if="isDirty"
          class="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded"
        >
          Modified
        </span>
        <span v-if="isSaving" class="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
          Saving...
        </span>
        <span
          v-if="saveSuccess"
          class="text-xs bg-green-200 text-green-800 px-2 py-1 rounded"
        >
          Saved ✓
        </span>
      </div>
    </div>

    <!-- Collapsible content -->
    <div v-if="isExpanded" class="px-4 pb-4">
      <!-- Permissions checkboxes -->
      <BaseCheckboxGroup
        v-model="selectedPermissions"
        :options="portalPermissionOptions"
        label=""
        class="md:flex flex-wrap"
        checkbox-class="basis-1/2 xl:basis-1/3"
        :disabled="disabled || isSaving"
      />

      <!-- Error message -->
      <div v-if="saveError" class="mt-2 p-2 bg-red-100 border border-red-300 rounded">
        <p class="text-sm text-red-800">{{ saveError }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { usePersonStore } from "../store/personStore";
import {
  SetPortalPermissionsRequest,
  PermissionType,
} from "../store/generatedFetchClient";

// Props
const props = defineProps<{
  portalId: number;
  portalName: string;
  personId: number;
  initialPermissions: PermissionType[];
  disabled?: boolean;
}>();

// Emits
const emit = defineEmits<{
  "save-success": [];
  "save-error": [error: string];
  "dirty-change": [isDirty: boolean];
}>();

// Store
const personStore = usePersonStore();

// State
const selectedPermissions = ref<PermissionType[]>([]);
const initialPerms = ref<PermissionType[]>([]);
const isSaving = ref(false);
const saveError = ref("");
const saveSuccess = ref(false);
const isExpanded = ref(false);

// Computed
const hasRegistration = computed(() => props.initialPermissions.length > 0);

const isDirty = computed(() => {
  if (selectedPermissions.value.length !== initialPerms.value.length) return true;
  return !selectedPermissions.value.every((p) => initialPerms.value.includes(p));
});

const panelClasses = computed(() => ({
  "border-gray-300": !isDirty.value && !saveError.value,
  "border-yellow-400 bg-yellow-50": isDirty.value && !saveError.value,
  "border-red-400 bg-red-50": saveError.value,
  "border-green-400 bg-green-50": saveSuccess.value && !isDirty.value,
  "opacity-60": props.disabled || isSaving.value,
}));

// Permission options
const portalPermissionOptions = [
  { name: "Can View Training Report", value: PermissionType.ViewTrainingReport },
  { name: "Can View Portal Roster", value: PermissionType.ViewPortalRoster },
  { name: "Can View Mobility Report", value: PermissionType.ViewMobilityReport },
  { name: "Can View VR Report", value: PermissionType.ViewVRReport },
  { name: "Can View VR Filter Report", value: PermissionType.ViewVRFilterReport },
  {
    name: "Can View Tenant Contact Report",
    value: PermissionType.ViewTenantContactReport,
  },
  { name: "Can View Users", value: PermissionType.ViewUsers },
  { name: "Can Edit Users", value: PermissionType.EditUsers },
  { name: "Can Edit Documents", value: PermissionType.EditDocuments },
  { name: "Can Edit Contacts", value: PermissionType.EditContacts },
  { name: "Can View Alert Subscriptions", value: PermissionType.ViewAlertSubscriptions },
  { name: "Can Send Alerts", value: PermissionType.SendAlerts },
  { name: "Can Edit Respond Content", value: PermissionType.EditRespond },
  { name: "Can Edit Tenants", value: PermissionType.EditTenant },
];

// Initialize
onMounted(() => {
  initialPerms.value = [...props.initialPermissions];
  selectedPermissions.value = [...props.initialPermissions];
  // Start collapsed if no permissions
  isExpanded.value = props.initialPermissions.length > 0;
});

// Toggle expanded state
function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

// Watch for dirty state changes
watch(isDirty, (newVal) => {
  emit("dirty-change", newVal);
  if (newVal) {
    saveSuccess.value = false;
  }
});

// Public methods (exposed to parent)
async function save(): Promise<boolean> {
  if (!isDirty.value) return true; // Nothing to save

  isSaving.value = true;
  saveError.value = "";
  saveSuccess.value = false;

  try {
    const request: SetPortalPermissionsRequest = {
      personId: props.personId,
      portalId: props.portalId,
      permissions: selectedPermissions.value.map((p) => p as number),
    };

    const error = await personStore.setPortalPermissions(request);

    if (error) {
      saveError.value = error;
      emit("save-error", error);
      isSaving.value = false;
      return false;
    }

    // Update initial state
    initialPerms.value = [...selectedPermissions.value];
    saveSuccess.value = true;
    emit("save-success");
    isSaving.value = false;
    return true;
  } catch (error: any) {
    const errorMsg = error?.message || "Unknown error occurred";
    saveError.value = errorMsg;
    emit("save-error", errorMsg);
    isSaving.value = false;
    return false;
  }
}

function reset() {
  selectedPermissions.value = [...initialPerms.value];
  saveError.value = "";
  saveSuccess.value = false;
}

function getIsDirty(): boolean {
  return isDirty.value;
}

// Expose methods to parent
defineExpose({
  save,
  reset,
  isDirty: getIsDirty,
});
</script>
